import { useTranslation } from "react-i18next"

import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { MutationSchema, MutationTypename } from "shared/api/types"
import { generateUUID } from "shared/lib/generateUUID"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { CreateTeamInput } from "shared/types/__generated__/graphql"
import { CreateTeamInputSchema } from "shared/types/__generated__/validation"

import z from "zod"

import {
  CreateTeamDocument,
  CreateTeamMutation,
  CreateTeamMutationVariables
} from "../graphql/mutations.generated"
import { useTeamsLazyQuery } from "../graphql/queries.generated"
import { createTeamInCache } from "../lib/createTeamInCache"
import { evictTeam } from "../lib/evictTeam"
import { Team, TeamSchema, TeamTypename } from "../types/Team"

const CreateTeamVariablesSchema = z.object({ input: CreateTeamInputSchema() })
const CreateTeamResponseSchema = MutationSchema.extend({ team: TeamSchema })

export function useCreateTeam() {
  const { cache: apolloCache } = useApolloClient()
  const { t } = useTranslation()
  const [apolloMutate, rest] = useApolloMutation<
    CreateTeamMutation,
    CreateTeamMutationVariables
  >(CreateTeamDocument)
  const [refetchTeams] = useTeamsLazyQuery()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Team.CREATE_PROJECT_TEAM.Start"),
      success: t("Request.Team.CREATE_PROJECT_TEAM.Success"),
      failure: t("Request.Team.CREATE_PROJECT_TEAM.Failure")
    }
  })

  const handleCreateTeam = useEvent(
    (
      { name, description }: { name: string; description?: string },
      options?: { suppressToasts?: boolean }
    ) => {
      const id = generateUUID()

      const input: CreateTeamInput = {
        id,
        name,
        description
      }

      const optimisticResponse: Team = {
        __typename: TeamTypename,
        ...input,
        id,
        memberships: [],
        projects: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast({
        suppress: !!options?.suppressToasts
      })

      return callMutation(apolloMutate, {
        variables: { input },
        transform: data => data.team,
        schemas: {
          input: CreateTeamVariablesSchema,
          response: CreateTeamResponseSchema
        },
        apolloOptions: {
          optimisticResponse: {
            __typename: MutationTypename,
            team: optimisticResponse
          },
          update: cache => {
            createTeamInCache({ cache, team: optimisticResponse })
          },
          onError: () => {
            evictTeam(id, apolloCache)
            raiseErrorToast()
          },
          onCompleted: () => {
            refetchTeams()
            raiseSuccessToast()
          }
        }
      })
    }
  )

  return {
    createTeam: handleCreateTeam,
    ...rest
  }
}
